/**
 * src/pages/GlossaryAndReferences/sections/HowToUseSection.tsx
 * Pedagogical orienting guide: How to navigate the glossary, how anchor targets work,
 * and the crucial distinction between fictional illustrative examples vs real empirical sources.
 */

import React from "react";
import { LessonStep } from "@/components/knowledge/LessonStep";
import { ScopeNote } from "@/components/knowledge/ScopeNote";
import { Deeper } from "@/components/knowledge/Deeper";
import { Compass, BookCheck, ShieldCheck } from "lucide-react";
export function HowToUseSection() {
  return <section className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 py-6 mb-8 min-w-0">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        {/* Left Column: How to Use */}
        <div className="lg:col-span-7 flex flex-col justify-between min-w-0">
          <LessonStep kind="explanation" title="How to Navigate This Library">
            <p className="text-muted-foreground">
              This repository connects foundational vocabulary across logic, epistemology, philosophy of science, and critical argumentation. Every term links to its structural siblings and cross-references interactive exercises across the Architecture of Knowledge.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90">
              <li>
                <strong className="text-foreground">In-Page Anchors:</strong> Clicking any related term or an incoming reference immediately focuses and highlights the definition via its unique <code className="text-primary font-mono text-xs">#term-&lt;id&gt;</code> hash.
              </li>
              <li>
                <strong className="text-foreground">Cross-Disciplinary Links:</strong> Each card's &ldquo;Explained in&rdquo; pill routes directly to the dedicated laboratory or workshop where that concept is tested with working models.
              </li>
              <li>
                <strong className="text-foreground">Epistemic Status:</strong> Each entry carries an explicit epistemic badge clarifying whether it is an axiomatic logical result, an empirical finding, or a normative philosophical argument.
              </li>
            </ul>
          </LessonStep>
        </div>

        {/* Right Column: Fictional Examples vs Real Sources */}
        <div className="lg:col-span-5 flex flex-col justify-between min-w-0">
          <LessonStep kind="mistake" title="Fictional Demonstrations vs. Real Sources">
            <p className="text-muted-foreground text-sm">
              Pedagogical examples used in definitions are designed as <strong className="text-foreground">stylized, simplified thought experiments</strong> or toy scenarios to isolate logical mechanics. They should never be confused with historical or clinical facts.
            </p>
            <p className="text-muted-foreground text-sm">
              Conversely, entries in the <strong className="text-foreground">Reading & Reference Library</strong> below represent genuine peer-reviewed monographs, historical treatises, and canonical philosophy texts.
            </p>
          </LessonStep>
        </div>
      </div>

      {/* Scope disclaimer */}
      <div className="mt-4">
        <ScopeNote>
          <div className="flex items-center gap-2 font-mono text-xs text-primary font-bold uppercase tracking-wider mb-1">
            <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
            <span>Citation & Verification Boundary</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Links and publication details were written from well-known sources and were not checked live on this site; check the publisher or library record before citing.
          </p>
        </ScopeNote>
      </div>
    </section>;
}